import React, { useMemo, useState } from "react";
import { AxiosError } from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getApiFieldErrors } from "@/services/api";
import { Loader2, RotateCcw, Save } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input, controlSizes } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import TrialLocked from "@/components/trial/TrialLocked";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ORGANIZATION_SETTINGS_QUERY_KEY } from "@/hooks/queries";
import { cn } from "@/lib/utils";
import {
  getOrganizationSettingsErrorMessage,
  isUnderwritingRangeValue,
  organizationSettingsService,
  type OrganizationSettings,
  type UnderwritingRangeValue,
  type UnderwritingRuleSchemaItem,
  type UnderwritingRulesPayload,
} from "@/services/organizationSettings";

const GROUP_LABELS: Record<string, string> = {
  leverage: "Leverage",
  funding: "Funding terms",
  hard_decline: "Hard-decline",
  llm: "Analysis table",
};

const GROUP_ORDER = ["leverage", "funding", "hard_decline", "llm"];

const NUMERIC_TYPES = new Set(["percent", "money", "int", "factor", "rate", "days"]);

type RangeDraft = { min: string; max: string };
type DraftValue = string | RangeDraft;
type DraftMap = Record<string, DraftValue>;

interface UnderwritingRulesCardProps {
  settings: OrganizationSettings | undefined;
  loading: boolean;
}

function formatDisplay(raw: string | number | boolean | undefined): string {
  if (raw == null) return "";
  if (typeof raw === "boolean") return raw ? "true" : "false";
  return String(raw);
}

function stripGrouping(raw: string): string {
  return raw.replace(/,/g, "");
}

function numericType(item: UnderwritingRuleSchemaItem): string {
  return item.type === "range" ? (item.endpoint_type ?? "money") : item.type;
}

function formatNumericDisplay(item: UnderwritingRuleSchemaItem, raw: string): string {
  const plain = stripGrouping(raw).trim();
  const value = Number(plain);
  if (!NUMERIC_TYPES.has(numericType(item)) || plain === "" || !Number.isFinite(value)) return raw;
  if (numericType(item) !== "money" && Math.abs(value) < 1000) return plain;
  return new Intl.NumberFormat("en-US", {
    useGrouping: true,
    maximumFractionDigits: 20,
  }).format(value);
}

function formatRange(
  item: UnderwritingRuleSchemaItem,
  raw: UnderwritingRangeValue | undefined
): RangeDraft {
  if (!raw || !isUnderwritingRangeValue(raw)) return { min: "", max: "" };
  return {
    min: formatNumericDisplay(item, formatDisplay(raw.min)),
    max: formatNumericDisplay(item, formatDisplay(raw.max)),
  };
}

function asRangeValue(raw: unknown): UnderwritingRangeValue | undefined {
  return isUnderwritingRangeValue(raw) ? raw : undefined;
}

function effectiveValue(raw: string, fallback: string): string {
  return raw.trim() === "" ? fallback : raw.trim();
}

function effectiveRange(raw: RangeDraft | undefined, fallback: RangeDraft): RangeDraft {
  return {
    min: effectiveValue(raw?.min ?? "", fallback.min),
    max: effectiveValue(raw?.max ?? "", fallback.max),
  };
}

function valuesFromBlob(blob: UnderwritingRulesPayload | undefined): DraftMap {
  if (!blob) return {};
  const next: DraftMap = {};
  for (const item of blob.schema) {
    if (item.type === "range") {
      next[item.id] = formatRange(item, asRangeValue(blob.values[item.id]));
    } else {
      const display = formatDisplay(blob.values[item.id] as string | number | boolean | undefined);
      next[item.id] = formatNumericDisplay(item, display);
    }
  }
  return next;
}

function clientScalarReason(item: UnderwritingRuleSchemaItem, raw: string): string | null {
  if (item.type === "enum") {
    return (item.enum ?? []).includes(raw) ? null : "Must be one of the allowed values";
  }
  if (item.type === "bool") {
    return raw === "true" || raw === "false" ? null : "Expected true or false";
  }
  const n = Number(stripGrouping(raw));
  if (!Number.isFinite(n)) return "Expected a finite number";
  if ((item.type === "int" || item.type === "days") && !Number.isInteger(n)) {
    return "Expected an integer";
  }
  if (item.min != null && n < Number(item.min)) return `Must be at least ${item.min}`;
  if (item.max != null && n > Number(item.max)) return `Must be at most ${item.max}`;
  return null;
}

function endpointCeiling(
  item: UnderwritingRuleSchemaItem,
  bound: "min" | "max"
): string | number | null {
  const listed = item.endpoint_max?.[bound === "min" ? 0 : 1];
  return listed ?? item.max;
}

function clientValueReason(item: UnderwritingRuleSchemaItem, raw: DraftValue): string | null {
  if (item.type === "range") {
    if (typeof raw === "string") return "Expected min and max";
    const minReason = clientScalarReason(
      { ...item, type: item.endpoint_type ?? "money", max: endpointCeiling(item, "min") },
      raw.min
    );
    if (minReason) return `Min: ${minReason}`;
    const maxReason = clientScalarReason(
      { ...item, type: item.endpoint_type ?? "money", max: endpointCeiling(item, "max") },
      raw.max
    );
    if (maxReason) return `Max: ${maxReason}`;
    if (Number(stripGrouping(raw.min)) > Number(stripGrouping(raw.max))) {
      return "Min must be less than or equal to max";
    }
    return null;
  }
  if (typeof raw !== "string") return "Expected a value";
  return clientScalarReason(item, raw);
}

function descriptionId(item: UnderwritingRuleSchemaItem): string {
  return `uw-rule-${item.id}-desc`;
}

function catalogChrome(item: UnderwritingRuleSchemaItem): {
  prefix: string | null;
  unit: string | null;
} {
  const raw = item.suffix?.trim() ?? "";
  if (raw.startsWith("$")) {
    const after = raw.slice(1).trim();
    return { prefix: "$", unit: after || null };
  }
  if (numericType(item) === "money") return { prefix: "$", unit: raw || null };
  return { prefix: null, unit: raw || null };
}

function ChromeField({
  prefix,
  suffix,
  unit,
  description,
  children,
}: {
  prefix?: string | null;
  suffix?: string | null;
  unit?: string | null;
  description?: string | null;
  children: React.ReactElement;
}) {
  const right = [unit, suffix].filter(Boolean).join(" ");
  const hasChrome = Boolean(prefix || right);
  const padded = React.cloneElement(children, {
    className: cn(
      (children.props as { className?: string }).className,
      "min-w-0",
      hasChrome
        ? "h-full w-auto flex-1 border-0 bg-transparent px-1.5 shadow-none focus-visible:ring-0"
        : "w-full"
    ),
  });
  const control = description ? (
    <Tooltip>
      <TooltipTrigger asChild>{padded}</TooltipTrigger>
      <TooltipContent className="max-w-xs">{description}</TooltipContent>
    </Tooltip>
  ) : (
    padded
  );
  return (
    <div
      className={cn(
        "relative min-w-0 flex-1",
        hasChrome && [
          controlSizes.default,
          "flex items-center rounded-md border border-input bg-background shadow-sm focus-within:border-primary focus-within:ring-[3px] focus-within:ring-primary/20",
        ]
      )}
    >
      {prefix ? (
        <span
          aria-hidden
          className="pointer-events-none shrink-0 pl-3 text-xs text-muted-foreground"
        >
          {prefix}
        </span>
      ) : null}
      {control}
      {right ? (
        <span
          aria-hidden
          className="pointer-events-none flex shrink-0 items-center gap-1 pr-3 text-xs text-muted-foreground"
        >
          {unit ? <span>{unit}</span> : null}
          {suffix ? <span>{suffix}</span> : null}
        </span>
      ) : null}
    </div>
  );
}

function nestedRuleMessages(value: unknown): Record<string, string> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const obj = value as Record<string, unknown>;
  const nested = obj.underwriting_rules;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return nestedRuleMessages(nested);
  }
  const result: Record<string, string> = {};
  for (const [key, raw] of Object.entries(obj)) {
    if (Array.isArray(raw) && typeof raw[0] === "string") result[key] = raw[0];
    else if (typeof raw === "string" && raw) result[key] = raw;
  }
  return result;
}

function fieldErrorsFromPatch(error: unknown): Record<string, string> {
  if (!(error instanceof AxiosError) || !error.response?.data) return {};
  const data = error.response.data as Record<string, unknown>;
  const details =
    data.details && typeof data.details === "object" && !Array.isArray(data.details)
      ? (data.details as Record<string, unknown>)
      : data;
  const fromEnvelope = {
    ...nestedRuleMessages(details),
    ...nestedRuleMessages(data),
  };
  const { underwriting_rules: _ignored, ...rest } = {
    ...getApiFieldErrors(error),
    ...fromEnvelope,
  };
  return rest;
}

const UnderwritingRulesCard: React.FC<UnderwritingRulesCardProps> = ({ settings, loading }) => {
  const queryClient = useQueryClient();
  const blob = settings?.underwriting_rules;
  const schema = useMemo(() => blob?.schema ?? [], [blob?.schema]);
  const [draft, setDraft] = useState<DraftMap | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [serverFieldErrors, setServerFieldErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);

  const values = draft ?? valuesFromBlob(blob);

  const groups = useMemo(() => {
    const byGroup = new Map<string, UnderwritingRuleSchemaItem[]>();
    for (const item of schema) {
      const list = byGroup.get(item.group) ?? [];
      list.push(item);
      byGroup.set(item.group, list);
    }
    const ordered = GROUP_ORDER.filter((g) => byGroup.has(g));
    for (const g of byGroup.keys()) {
      if (!ordered.includes(g)) ordered.push(g);
    }
    return ordered.map((id) => ({ id, items: byGroup.get(id) ?? [] }));
  }, [schema]);

  const dirtyPayload = useMemo(() => {
    if (!blob) return {};
    const patch: Record<string, string | boolean | UnderwritingRangeValue> = {};
    for (const item of blob.schema) {
      if (!(item.id in values)) continue;
      if (item.type === "range") {
        const fallback = formatRange(item, asRangeValue(blob.defaults[item.id]));
        const current = effectiveRange(values[item.id] as RangeDraft | undefined, fallback);
        const savedValue = formatRange(item, asRangeValue(blob.values[item.id]));
        const plain = {
          min: stripGrouping(current.min),
          max: stripGrouping(current.max),
        };
        if (
          plain.min === stripGrouping(savedValue.min) &&
          plain.max === stripGrouping(savedValue.max)
        ) {
          continue;
        }
        patch[item.id] = plain;
        continue;
      }
      const fallback = formatDisplay(
        blob.defaults[item.id] as string | number | boolean | undefined
      );
      const current = stripGrouping(effectiveValue(String(values[item.id] ?? ""), fallback));
      const savedValue = formatDisplay(
        blob.values[item.id] as string | number | boolean | undefined
      );
      if (current === stripGrouping(savedValue)) continue;
      patch[item.id] = item.type === "bool" ? current === "true" : current;
    }
    return patch;
  }, [blob, values]);

  const dirty = Object.keys(dirtyPayload).length > 0;
  const fieldErrors = useMemo(() => {
    if (!blob || !dirty) return { ...serverFieldErrors };
    const errors: Record<string, string> = { ...serverFieldErrors };
    for (const item of blob.schema) {
      const current = currentDraftValue(item, values, blob);
      const reason = clientValueReason(item, current);
      if (reason) errors[item.id] = reason;
    }
    return errors;
  }, [blob, dirty, values, serverFieldErrors]);
  const clientInvalid = useMemo(() => {
    if (!blob || !dirty) return false;
    for (const item of blob.schema) {
      if (clientValueReason(item, currentDraftValue(item, values, blob))) return true;
    }
    return false;
  }, [blob, dirty, values]);

  const setValues = (action: React.SetStateAction<DraftMap>) => {
    setServerFieldErrors({});
    setDraft((prev) => {
      const base = prev ?? valuesFromBlob(blob);
      return typeof action === "function" ? action(base) : action;
    });
  };

  const mutation = useMutation({
    mutationFn: () => organizationSettingsService.update({ underwriting_rules: dirtyPayload }),
    onMutate: () => {
      setError(null);
      setServerFieldErrors({});
      setSaved(false);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(ORGANIZATION_SETTINGS_QUERY_KEY, data);
      setDraft(valuesFromBlob(data.underwriting_rules));
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    },
    onError: (err) => {
      const nested = fieldErrorsFromPatch(err);
      setServerFieldErrors(nested);
      setError(getOrganizationSettingsErrorMessage(err));
    },
  });

  const resetField = (item: UnderwritingRuleSchemaItem) => {
    if (item.type === "range") {
      setValues((prev) => ({
        ...prev,
        [item.id]: formatRange(item, asRangeValue(blob?.defaults[item.id])),
      }));
      return;
    }
    setValues((prev) => ({
      ...prev,
      [item.id]: formatNumericDisplay(
        item,
        formatDisplay(blob?.defaults[item.id] as string | number | boolean | undefined)
      ),
    }));
  };

  const describedBy = (item: UnderwritingRuleSchemaItem) =>
    item.description ? descriptionId(item) : undefined;

  const renderControl = (item: UnderwritingRuleSchemaItem) => {
    const disabled = loading || mutation.isPending;
    const invalid = Boolean(fieldErrors[item.id]);
    const described = describedBy(item);
    if (item.type === "enum") {
      return (
        <Select
          value={String(values[item.id] ?? "")}
          onValueChange={(value) => setValues((prev) => ({ ...prev, [item.id]: value }))}
          disabled={disabled}
        >
          <ChromeField description={item.description}>
            <span className="inline-flex w-full min-w-0">
              <SelectTrigger
                id={`uw-rule-${item.id}`}
                aria-invalid={invalid}
                aria-describedby={described}
              >
                <SelectValue />
              </SelectTrigger>
            </span>
          </ChromeField>
          <SelectContent>
            {(item.enum ?? []).map((choice) => (
              <SelectItem key={choice} value={choice}>
                {choice.charAt(0).toUpperCase() + choice.slice(1)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    if (item.type === "bool") {
      const checked = values[item.id] === "true";
      return (
        <ChromeField description={item.description}>
          <span className="inline-flex min-w-0">
            <Switch
              id={`uw-rule-${item.id}`}
              checked={checked}
              onCheckedChange={(next) =>
                setValues((prev) => ({ ...prev, [item.id]: next ? "true" : "false" }))
              }
              disabled={disabled}
              aria-invalid={invalid}
              aria-describedby={described}
            />
          </span>
        </ChromeField>
      );
    }
    if (item.type === "range") {
      const pair = (values[item.id] as RangeDraft | undefined) ?? { min: "", max: "" };
      const numeric = NUMERIC_TYPES.has(item.endpoint_type ?? "money");
      const { prefix, unit } = catalogChrome(item);
      const endpointInput = (bound: "min" | "max") => (
        <ChromeField
          prefix={prefix}
          unit={unit}
          suffix={bound === "min" ? "Min" : "Max"}
          description={item.description}
        >
          <Input
            id={`uw-rule-${item.id}-${bound}`}
            type="text"
            inputMode={numeric ? "decimal" : undefined}
            min={item.min == null ? undefined : Number(item.min)}
            max={
              endpointCeiling(item, bound) == null
                ? undefined
                : Number(endpointCeiling(item, bound))
            }
            step={item.step == null ? undefined : Number(item.step)}
            value={pair[bound]}
            aria-label={`${item.label} ${bound}`}
            aria-describedby={described}
            onFocus={() => {
              setValues((prev) => ({
                ...prev,
                [item.id]: {
                  ...((prev[item.id] as RangeDraft) ?? pair),
                  [bound]: stripGrouping(
                    ((prev[item.id] as RangeDraft | undefined) ?? pair)[bound]
                  ),
                },
              }));
            }}
            onChange={(e) =>
              setValues((prev) => ({
                ...prev,
                [item.id]: {
                  ...((prev[item.id] as RangeDraft) ?? pair),
                  [bound]: e.target.value,
                },
              }))
            }
            onBlur={() => {
              setValues((prev) => {
                const current = (prev[item.id] as RangeDraft | undefined) ?? pair;
                return {
                  ...prev,
                  [item.id]: {
                    ...current,
                    [bound]: formatNumericDisplay(item, current[bound]),
                  },
                };
              });
            }}
            disabled={disabled}
            aria-invalid={invalid}
          />
        </ChromeField>
      );
      return (
        <div className="flex min-w-0 flex-1 gap-2">
          {endpointInput("min")}
          {endpointInput("max")}
        </div>
      );
    }
    const { prefix, unit } = catalogChrome(item);
    return (
      <ChromeField prefix={prefix} unit={unit} description={item.description}>
        <Input
          id={`uw-rule-${item.id}`}
          type="text"
          inputMode={NUMERIC_TYPES.has(item.type) ? "decimal" : undefined}
          min={item.min == null ? undefined : Number(item.min)}
          max={item.max == null ? undefined : Number(item.max)}
          step={item.step == null ? undefined : Number(item.step)}
          value={String(values[item.id] ?? "")}
          aria-describedby={described}
          onFocus={() => {
            setValues((prev) => ({
              ...prev,
              [item.id]: stripGrouping(String(prev[item.id] ?? "")),
            }));
          }}
          onChange={(e) => setValues((prev) => ({ ...prev, [item.id]: e.target.value }))}
          onBlur={() => {
            setValues((prev) => ({
              ...prev,
              [item.id]: formatNumericDisplay(item, String(prev[item.id] ?? "")),
            }));
          }}
          disabled={disabled}
          aria-invalid={invalid}
        />
      </ChromeField>
    );
  };

  return (
    <Card data-testid="underwriting-rules">
      <CardHeader>
        <CardTitle>Underwriting rules</CardTitle>
        <CardDescription>
          Organization overrides for registered underwriting thresholds. Unchanged values stay on
          platform defaults.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {loading && !blob ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading underwriting rules…
          </div>
        ) : (
          <TrialLocked lock="underwritingRules">
            <TooltipProvider delayDuration={150}>
              <div className="space-y-6">
                {groups.map((group) => (
                  <div key={group.id} className="space-y-3">
                    <h3 className="text-sm font-semibold">{GROUP_LABELS[group.id] ?? group.id}</h3>
                    <div className="grid gap-3 sm:grid-cols-3">
                      {group.items.map((item) => (
                        <div key={item.id} className="min-w-0 space-y-1">
                          <Label
                            htmlFor={
                              item.type === "range"
                                ? `uw-rule-${item.id}-min`
                                : `uw-rule-${item.id}`
                            }
                          >
                            {item.label}
                          </Label>
                          {item.description ? (
                            <span id={descriptionId(item)} className="sr-only">
                              {item.description}
                            </span>
                          ) : null}
                          <div className="flex min-w-0 items-center gap-2">
                            {renderControl(item)}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="shrink-0"
                              onClick={() => resetField(item)}
                              aria-label={`Reset ${item.label} to default`}
                              disabled={loading || mutation.isPending}
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          </div>
                          {fieldErrors[item.id] && (
                            <p className="text-xs text-destructive">{fieldErrors[item.id]}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => mutation.mutate()}
                    disabled={loading || mutation.isPending || !dirty || clientInvalid}
                  >
                    {mutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    {mutation.isPending ? "Saving..." : "Save"}
                  </Button>
                  {saved && <span className="text-xs text-muted-foreground">Saved.</span>}
                </div>
              </div>
            </TooltipProvider>
          </TrialLocked>
        )}
      </CardContent>
    </Card>
  );
};

function currentDraftValue(
  item: UnderwritingRuleSchemaItem,
  values: DraftMap,
  blob: UnderwritingRulesPayload
): DraftValue {
  if (item.type === "range") {
    const fallback = formatRange(item, asRangeValue(blob.defaults[item.id]));
    return effectiveRange(values[item.id] as RangeDraft | undefined, fallback);
  }
  const fallback = formatDisplay(blob.defaults[item.id] as string | number | boolean | undefined);
  return effectiveValue(String(values[item.id] ?? ""), fallback);
}

export default UnderwritingRulesCard;
